import React, { useMemo, useState } from "react";
import { createDecorators } from "@next-core/element";
import { ReactNextElement, wrapBrick } from "@next-core/react-element";
import "@next-core/theme";
import { initializeI18n } from "@next-core/i18n";
import { K, NS, locales, t } from "./i18n.js";
import styleText from "./styles.shadow.css";
import type {
  Tab,
  TabList,
  TabListEvents,
  TabListMapping,
  TabListProps,
} from "../tab-list/index.js";
import type { ShowCase, ShowCaseProps } from "../show-case/index.js";
import type { ShowCaseType } from "../shared/interfaces.js";

initializeI18n(NS, locales);

const WrappedShowCase = wrapBrick<ShowCase, ShowCaseProps>(
  "ai-portal.show-case"
);
const WrappedTabList = wrapBrick<
  TabList,
  TabListProps,
  TabListEvents,
  TabListMapping
>("ai-portal.tab-list", {
  onTabClick: "tab.click",
});

const { defineElement, property } = createDecorators();

export interface ShowCasesProps {
  list?: ShowCaseType[];
  limit?: number;
}

/**
 * 构件 `ai-portal.show-cases`
 *
 * 案例列表展示构件，支持按场景分组筛选，展示多个优秀案例卡片。
 */
export
@defineElement("ai-portal.show-cases", {
  styleTexts: [styleText],
})
class ShowCases extends ReactNextElement implements ShowCasesProps {
  /** 案例数据列表，每项可包含 scenario 字段用于分组筛选 */
  @property({ attribute: false })
  accessor list: ShowCaseType[] | undefined;

  /** 每个场景分组下最多显示的案例数量 */
  @property({ type: Number })
  accessor limit: number | undefined;

  render() {
    return <ShowCasesComponent list={this.list} limit={this.limit} />;
  }
}

function ShowCasesComponent({ list, limit }: ShowCasesProps) {
  // Grouping the list by scenario
  const groups = useMemo<string[]>(() => {
    return ["", ...new Set(list?.map((item) => item.scenario).filter(Boolean))];
  }, [list]);

  const [activeGroup, setActiveGroup] = useState("");

  const filteredList = useMemo(() => {
    if (!list) {
      return;
    }
    let filtered = list;
    if (activeGroup) {
      filtered = filtered.filter((item) => item.scenario === activeGroup);
    }
    if (limit) {
      filtered = filtered.slice(0, limit);
    }
    return filtered;
  }, [activeGroup, list, limit]);

  const tabs = useMemo<Tab[]>(() => {
    return groups.map((group) => ({
      id: group,
      label: group === "" ? t(K.ALL) : group,
    }));
  }, [groups]);

  if (!list?.length) {
    return null;
  }

  return (
    <>
      <div className="tips">{`${t(K.EXPLORE_EXCELLENT_CASES)} ↓`}</div>
      <WrappedTabList
        tabs={tabs}
        activeTab={activeGroup}
        onTabClick={(event) => setActiveGroup(event.detail.id)}
      />
      <ul className="cases">
        {filteredList?.map((item) => (
          <li key={item.conversationId}>
            <WrappedShowCase
              caseTitle={item.title}
              summary={item.summary}
              url={item.url}
            />
          </li>
        ))}
      </ul>
    </>
  );
}
